import React, { useEffect, useId, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  suffix?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/** −/поле/+ для целых чисел. Кнопки неактивны на границах. */
export const NumberStepper: React.FC<NumberStepperProps> = ({
  value,
  onChange,
  min = -Infinity,
  max = Infinity,
  step = 1,
  label,
  suffix,
  disabled,
  id,
  className,
}) => {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [draft, setDraft] = useState(String(value));

  useEffect(() => setDraft(String(value)), [value]);

  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v)));

  const commit = () => {
    const parsed = Number(draft.replace(',', '.').trim());
    if (draft.trim() === '' || Number.isNaN(parsed)) {
      setDraft(String(value));
      return;
    }
    const next = clamp(parsed);
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  const btn =
    'flex h-8 w-8 items-center justify-center text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent dark:text-zinc-300 dark:hover:bg-zinc-800';

  return (
    <div className={cn('inline-flex flex-col gap-1', className)}>
      {label && (
        <label htmlFor={inputId} className="text-xs font-medium text-gray-700 dark:text-zinc-300">
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <div className="inline-flex items-center overflow-hidden rounded-md border border-gray-300 bg-white dark:border-zinc-700 dark:bg-zinc-900">
          <button
            type="button"
            aria-label="Уменьшить"
            disabled={disabled || value <= min}
            onClick={() => onChange(clamp(value - step))}
            className={btn}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <input
            id={inputId}
            type="text"
            inputMode="numeric"
            value={draft}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                setDraft(String(value));
              }
            }}
            className="h-8 w-12 border-x border-gray-300 bg-transparent text-center font-mono text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-indigo-500 disabled:cursor-not-allowed dark:border-zinc-700 dark:text-zinc-100"
          />
          <button
            type="button"
            aria-label="Увеличить"
            disabled={disabled || value >= max}
            onClick={() => onChange(clamp(value + step))}
            className={btn}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        {suffix && <span className="text-xs text-gray-500 dark:text-zinc-400">{suffix}</span>}
      </div>
    </div>
  );
};

export default NumberStepper;
